import type { Fixture } from "./types.js";

/**
 * Americano: everybody partners as many different people as possible, and the points are
 * yours rather than your pair's (requirements §5.5).
 *
 * The whole schedule is known before a ball is hit, because nothing in it depends on a
 * result — which is the difference between this and Mexicano, where each round is drawn from
 * the standings after the last one and only one round can honestly exist at a time.
 *
 * **What this optimises, in order.** Everybody plays the same number of matches; then
 * partner variety; then opponent variety. That order is not arbitrary. Somebody who sat out
 * twice while another player got an extra match will say so before anyone notices they
 * partnered the same person twice, and a format whose selling point is "you play with
 * everyone" has to get the count right before it gets the mixture right.
 *
 * **Greedy rather than exhaustive.** Building the perfect schedule is a
 * social-golfer/1-factorisation problem — exact constructions exist when the numbers are
 * kind (n divisible by 4, m = n − 1) and nothing closed-form covers the rest. A greedy pass
 * that always takes the least-used partner and the least-used opponents lands on zero
 * repeats wherever zero is achievable at club sizes, and degrades honestly rather than
 * failing when it is not. The counts it settled for come back in the result, so an organiser
 * sees "3 pairs repeat" rather than being told the draw is perfect.
 */

export interface AmericanoOptions {
  /** How many matches each player gets. `n × m` has to divide by four — see below. */
  matchesPerPlayer: number;
  /** How many matches run at once. One, until the club has a second court. */
  courts?: number;
}

export interface Americano {
  fixtures: Fixture[];
  /** How many rounds the fixtures fall into. With one court, one match per round. */
  rounds: number;
  /** Fixtures whose pair had already partnered. Zero wherever zero is possible. */
  partnerRepeats: number;
  /** Player-vs-player meetings that had happened before. Beaten down after partners. */
  opponentRepeats: number;
  /** Difference between the most and least matches anybody gets. Zero by construction. */
  spread: number;
}

/**
 * The values of `m` a field of `n` can actually run.
 *
 * Every fixture is four slots, so `n × m` has to divide by four — and that bites in a way
 * organisers do not expect. Six players cannot play three each (18 slots, four and a half
 * matches); they play two each or four each. Ten players can play two, four, six or eight
 * each and never an odd number.
 */
export function validAmericanoMatches(n: number): number[] {
  const valid: number[] = [];
  for (let m = 1; m <= Math.max(1, n - 1); m++) if ((n * m) % 4 === 0) valid.push(m);
  return valid;
}

/** Four slots a fixture, so this is exact rather than a ceiling. */
export function americanoFixtureCount(n: number, m: number): number {
  return (n * m) / 4;
}

export function americanoDraw(players: string[], options: AmericanoOptions): Americano {
  const n = players.length;
  const m = options.matchesPerPlayer;
  const courts = Math.max(1, options.courts ?? 1);

  if (n < 4) throw new Error("an americano needs at least four players");
  if (m < 1) throw new Error("every player must play at least one match");
  if ((n * m) % 4 !== 0) {
    const options = validAmericanoMatches(n).join(" or ");
    throw new Error(`${n} players cannot play ${m} matches each — try ${options}`);
  }

  const total = americanoFixtureCount(n, m);
  const perRound = Math.min(courts, Math.floor(n / 4));

  // How often each pair of players has been on the same side, and on opposite sides. Square
  // and symmetric; the diagonal is never read.
  const partnered = matrix(n);
  const opposed = matrix(n);
  const played = Array.from({ length: n }, () => 0);
  // Rounds since somebody last played, so that when two people are equally short of matches
  // the one who has been watching longer goes on. Without it the same person sits out the
  // first two rounds of every draw, which is the complaint this format exists to avoid.
  const waiting = Array.from({ length: n }, () => 0);

  /** Each match as [partner, partner, opponent, opponent], by index into `players`. */
  const quartets: Quartet[] = [];
  const rounds: number[] = [];
  let round = 0;

  while (quartets.length < total) {
    round += 1;
    const courtsNow = Math.min(perRound, total - quartets.length);
    const pool = new Set(eligible(courtsNow * 4, played, waiting));
    const playing: number[] = [];

    for (let court = 0; court < courtsNow; court++) {
      const anchor = leastPlayed([...pool], played, waiting);
      pool.delete(anchor);

      const partner = least([...pool], (x) => [partnered[anchor]![x]!, opposed[anchor]![x]!, x]);
      pool.delete(partner);

      const [left, right] = bestOpponents([...pool], anchor, partner, partnered, opposed);
      pool.delete(left);
      pool.delete(right);

      for (const [x, y] of [
        [anchor, partner],
        [left, right],
      ] as const) {
        partnered[x]![y]! += 1;
        partnered[y]![x]! += 1;
      }
      for (const x of [anchor, partner]) {
        for (const y of [left, right]) {
          opposed[x]![y]! += 1;
          opposed[y]![x]! += 1;
        }
      }
      for (const x of [anchor, partner, left, right]) {
        played[x]! += 1;
        playing.push(x);
      }

      quartets.push([anchor, partner, left, right]);
      rounds.push(round);
    }

    for (let i = 0; i < n; i++) waiting[i] = playing.includes(i) ? 0 : waiting[i]! + 1;
  }

  polish(quartets, rounds, n);

  const counted = tally(quartets, n);

  return {
    fixtures: quartets.map((quartet, index) => ({
      round: rounds[index] as number,
      sequence: index + 1,
      sideA: {
        kind: "players",
        memberIds: [players[quartet[0]] as string, players[quartet[1]] as string],
      },
      sideB: {
        kind: "players",
        memberIds: [players[quartet[2]] as string, players[quartet[3]] as string],
      },
    })),
    rounds: round,
    partnerRepeats: repeats(counted.partnered),
    opponentRepeats: repeats(counted.opposed),
    spread: Math.max(...played) - Math.min(...played),
  };
}

type Quartet = [number, number, number, number];

/**
 * Swap players between matches while it makes the mixture better.
 *
 * The greedy above builds each round from whoever is owed a match, and the last foursome of
 * every cycle is therefore forced: three matches' worth of players have already been taken
 * and the four left over play each other whatever their history. At sixteen players and one
 * court that left one repeated partnership in a draw where none was necessary.
 *
 * A swap moves one player into another match and one back, so **every player still plays
 * exactly as many matches as before** — the fairness the greedy worked for cannot be undone
 * here, which is why the fix is a swap rather than a rebuild. The only extra condition is
 * that nobody ends up in two matches of the same round.
 *
 * Repeated partners cost a hundred times a repeated opponent, so the search will happily
 * take four new opponents' worth of damage to avoid partnering somebody twice. That is the
 * format's own priority: "everyone partners with as many different people as possible".
 */
function polish(quartets: Quartet[], rounds: number[], n: number): void {
  for (let pass = 0; pass < 40; pass++) {
    let improved = false;
    let cost = costOf(quartets, n);

    for (let i = 0; i < quartets.length && !improved; i++) {
      for (let j = i + 1; j < quartets.length && !improved; j++) {
        for (let p = 0; p < 4 && !improved; p++) {
          for (let q = 0; q < 4 && !improved; q++) {
            const x = quartets[i]![p] as number;
            const y = quartets[j]![q] as number;
            if (quartets[i]!.includes(y) || quartets[j]!.includes(x)) continue;
            if (clashes(quartets, rounds, j, x, i) || clashes(quartets, rounds, i, y, j)) continue;

            quartets[i]![p] = y;
            quartets[j]![q] = x;
            const after = costOf(quartets, n);

            if (after < cost) {
              cost = after;
              improved = true;
            } else {
              quartets[i]![p] = x;
              quartets[j]![q] = y;
            }
          }
        }
      }
    }

    if (!improved) return;
  }
}

/** Whether moving `player` into the match at `into` would put them on court twice at once. */
function clashes(
  quartets: Quartet[],
  rounds: number[],
  from: number,
  player: number,
  into: number,
) {
  if (rounds[from] === rounds[into]) return false;
  return quartets.some(
    (quartet, index) =>
      index !== into && rounds[index] === rounds[into] && quartet.includes(player),
  );
}

function tally(quartets: Quartet[], n: number) {
  const partnered = matrix(n);
  const opposed = matrix(n);

  for (const [a, b, c, d] of quartets) {
    for (const [x, y] of [
      [a, b],
      [c, d],
    ] as const) {
      partnered[x]![y]! += 1;
      partnered[y]![x]! += 1;
    }
    for (const x of [a, b]) {
      for (const y of [c, d]) {
        opposed[x]![y]! += 1;
        opposed[y]![x]! += 1;
      }
    }
  }

  return { partnered, opposed };
}

function costOf(quartets: Quartet[], n: number): number {
  const { partnered, opposed } = tally(quartets, n);
  return repeats(partnered) * 100 + repeats(opposed);
}

const matrix = (n: number) => Array.from({ length: n }, () => Array.from({ length: n }, () => 0));

/** Every meeting after the first one, counted once per pair rather than once per direction. */
function repeats(counts: number[][]): number {
  let extra = 0;
  for (let i = 0; i < counts.length; i++) {
    for (let j = i + 1; j < counts.length; j++) extra += Math.max(0, counts[i]![j]! - 1);
  }
  return extra;
}

/**
 * Who is available for this round: fewest matches so far, then longest sat out, then lowest
 * index — and then **everybody else on the same number of matches**.
 *
 * That last part is the difference between a rotation and a shuffle. Taking exactly the four
 * players who are next in line leaves no choice about who partners whom, so with one court
 * and sixteen players every foursome is forced and the partner counter is along for the
 * ride: the first attempt at this repeated eight partnerships in a draw where zero was
 * available. Handing the greedy the whole tier of equally-owed players gives it something to
 * choose from without letting anybody get an extra match — everyone in the tier has played
 * the same number, so whichever four go on, nobody is overtaken.
 *
 * The index tie-break is only there to make the draw the same every time it is generated
 * from the same entrants. A preview that showed a different rotation from the draw it
 * previewed would be worse than no preview.
 */
function eligible(count: number, played: number[], waiting: number[]): number[] {
  const order = played.map((_, index) => index).sort(compare((i) => [played[i]!, -waiting[i]!, i]));
  const last = order[count - 1] as number;
  return order.filter((i) => played[i]! <= played[last]!);
}

const leastPlayed = (pool: number[], played: number[], waiting: number[]): number =>
  least(pool, (i) => [played[i]!, -waiting[i]!, i]);

/**
 * The two opponents that have met these two least, and each other least.
 *
 * Partner variety comes first inside the pair as well: `partnered[left][right]` is weighted
 * alongside the four opponent counts, so a pairing that would repeat a partnership loses to
 * one that merely repeats an opponent.
 */
function bestOpponents(
  pool: number[],
  anchor: number,
  partner: number,
  partnered: number[][],
  opposed: number[][],
): [number, number] {
  let best: [number, number] = [pool[0] as number, pool[1] as number];
  let bestCost: number[] = [Infinity];

  for (let i = 0; i < pool.length; i++) {
    for (let j = i + 1; j < pool.length; j++) {
      const left = pool[i] as number;
      const right = pool[j] as number;
      const cost = [
        partnered[left]![right]!,
        opposed[anchor]![left]! +
          opposed[anchor]![right]! +
          opposed[partner]![left]! +
          opposed[partner]![right]!,
        left,
        right,
      ];
      if (lower(cost, bestCost)) {
        best = [left, right];
        bestCost = cost;
      }
    }
  }

  return best;
}

/** Lexicographic on the key, which is how every tie-break in here is expressed. */
const compare =
  (key: (i: number) => number[]) =>
  (a: number, b: number): number => {
    const x = key(a);
    const y = key(b);
    for (let i = 0; i < x.length; i++) {
      if (x[i] !== y[i]) return (x[i] as number) - (y[i] as number);
    }
    return 0;
  };

function least(pool: number[], key: (i: number) => number[]): number {
  let best = pool[0] as number;
  for (const candidate of pool.slice(1)) {
    if (lower(key(candidate), key(best))) best = candidate;
  }
  return best;
}

function lower(a: number[], b: number[]): boolean {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? Infinity;
    if (x !== y) return x < y;
  }
  return false;
}
